import type { CurrencyCode, UnknownEvent } from '@faststore/sdk'
import { sendAnalyticsEvent, useSession } from '@faststore/sdk'
import { useEffect, useRef, useState } from 'react'
import { trackDYAddToCart } from 'src/sdk/analytics/dynamicYield'
import { getPersonHashPromise } from 'src/sdk/analytics/hooks/useUserEvent'
import {
  useAuthenticatedUserToken,
  useUserToken,
} from 'src/sdk/analytics/hooks/useUserToken'
import type { AddItemsInput, AddItemsResult } from 'src/sdk/cart/addItems'
import {
  ADD_ITEMS_TIMEOUT_MS,
  buildCartItemFromProduct,
  CART_UPDATED_EVENT,
  checkSkuAvailability,
  fetchSkuProduct,
  sanitizeAddItemsInput,
} from 'src/sdk/cart/addItems'
import { addCartItem, addCartItems } from 'src/sdk/cart/handleCartItems'
import { useCart } from 'src/sdk/cart/useCart'
import type { CartItem } from 'src/sdk/cart/validate'
import { BK_ID_STORAGE_KEY } from 'src/sdk/session/validate'

export type AddItemsOptions = {
  // Nome lista per GA4 (item_list_name). Default: "Spesso comprati assieme".
  // Stringa vuota per non valorizzarlo.
  listName?: string
}

// Dati del prodotto della PDP corrente, esposti ai widget DY (es. "Spesso
// comprati assieme") che devono mostrarlo come prima card
export type ArcaplanetPdpContext = {
  sku: string
  productId: string
  name: string
  brand: string
  image: string
  price: number
  categories: string
  available: boolean
}

declare global {
  interface Window {
    arcaplanet?: {
      cart?: {
        addItems: (
          items: AddItemsInput[],
          options?: AddItemsOptions
        ) => Promise<AddItemsResult>
      }
      pdp?: ArcaplanetPdpContext
    }
  }
}

const trackAddedItems = (
  addedItems: CartItem[],
  cartId: string,
  cartItems: CartItem[],
  currency: CurrencyCode,
  listName: string | null
) => {
  if (addedItems.length === 0) {
    return
  }

  const analyticsItems = addedItems.map((item, index) => ({
    object_id: item.itemOffered.sku,
    index,
    item_list_id: '',
    ...(listName && { item_list_name: listName }),
    item_category: item.itemOffered.breadcrumbList?.itemListElement
      ?.slice(0, -1)
      .map((x) => x.name),
    item_id: item.itemOffered.isVariantOf.productGroupID,
    item_name: item.itemOffered.isVariantOf.name,
    item_brand: item.itemOffered.brand.name,
    item_variant: item.itemOffered.sku,
    quantity: item.quantity,
    unitMultiplier: item.itemOffered.productSpecifications?.unitMultiplier ?? 1,
    price: item.price,
    discount: +(item.listPrice - item.price).toFixed(2),
    full_price: item.listPrice,
    discount_percentage: +(1 - item.price / item.listPrice).toFixed(2),
    currency,
    item_variant_name: item.itemOffered.name,
    product_reference_id: Number(item.itemOffered.gtin),
    coupon: null,
  }))

  // GA4: un solo evento con tutti gli item aggiunti. Niente param
  // `dynamicYield`, cosi' l'handler DY dell'analytics non lo duplica.
  sendAnalyticsEvent<UnknownEvent>({
    name: 'add_to_cart',
    params: {
      orderFormId: cartId,
      currency,
      value: addedItems.reduce((acc, i) => acc + i.price * i.quantity, 0),
      items: analyticsItems,
    },
  })

  // DY: un Add to Cart (add-to-cart-v1) per item aggiunto
  addedItems.forEach((item, index) => {
    trackDYAddToCart({
      items: [analyticsItems[index]],
      dynamicYield: {
        currency,
        value: item.price * item.quantity,
        cartItems: [...cartItems, ...addedItems.slice(0, index)],
      },
    })
  })
}

// Componente senza UI: espone window.arcaplanet.cart.addItems per le campagne
// DY (es. "Spesso comprati assieme"). Contratto:
//   addItems([{ sku, qty }]) => Promise<{ added: [{sku, qty}], failed: [{sku, reason}] }>
//   reason: 'unavailable' | 'max_qty' | 'error'
// La Promise viene rigettata solo per errori di rete/timeout (10 s), mai per
// il fallimento del singolo item. A fine chiamata emette 'arcaplanet:cart:updated'.
const CartApiHandler = () => {
  const [emailHash, setEmailHash] = useState<string | null>(null)
  const { addItem, id: cartId, items } = useCart()
  const { channel, locale, currency } = useSession()
  const userToken = useUserToken()
  const authenticatedUserToken = useAuthenticatedUserToken()
  const { person } = useSession()
  const email = person?.email
  const bkId = JSON.parse(
    localStorage.getItem(BK_ID_STORAGE_KEY) || '{}'
  )?.bk_id

  useEffect(() => {
    let cancelled = false

    getPersonHashPromise(email)
      .then((hashedEmail) => {
        if (!cancelled) setEmailHash(hashedEmail)
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to hash email', error)
          setEmailHash(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [email])

  // Ref cosi' il listener su window e' registrato una volta sola ma legge sempre
  // i valori aggiornati degli hook
  const latest = useRef({
    addItem,
    cartId,
    items,
    channel,
    locale,
    currencyCode: currency.code,
    userToken,
    authenticatedUserToken,
    bk_account_id: bkId,
    vtex_user_id: person?.id,
    emailHash,
  })

  latest.current = {
    addItem,
    cartId,
    items,
    channel,
    locale,
    currencyCode: currency.code,
    userToken,
    authenticatedUserToken,
    bk_account_id: bkId,
    vtex_user_id: person?.id,
    emailHash,
  }

  useEffect(() => {
    // Le chiamate sono serializzate: addCartItem legge e riscrive l'orderForm
    let queue: Promise<unknown> = Promise.resolve()

    const run = async (
      inputs: AddItemsInput[],
      listName: string | null,
      signal: { aborted: boolean }
    ): Promise<AddItemsResult> => {
      const result: AddItemsResult = { added: [], failed: [] }
      const addedItems: CartItem[] = []
      const startItems = latest.current.items

      const { channel: ch, locale: loc } = latest.current

      const lookups = await Promise.all(
        inputs.map(async ({ sku, qty }) => {
          try {
            const product = await fetchSkuProduct(sku, ch ?? '', loc ?? '')

            return {
              sku,
              qty,
              product,
              check: checkSkuAvailability(product, qty),
            }
          } catch (error) {
            console.error('cart.addItems lookup failed for sku', sku, error)

            return { sku, qty, product: null, check: null }
          }
        })
      )

      if (signal.aborted) {
        return result
      }

      const toAdd: Array<{ sku: string; qty: number; item: CartItem }> = []

      lookups.forEach(({ sku, qty, product, check }) => {
        if (!check) {
          result.failed.push({ sku, reason: 'error' })
        } else if (!check.ok || !product) {
          result.failed.push({
            sku,
            reason: check.ok ? 'unavailable' : check.reason,
          })
        } else {
          toAdd.push({
            sku,
            qty,
            item: buildCartItemFromProduct(product, qty, {
              userToken: latest.current.userToken,
              authenticatedUserToken: latest.current.authenticatedUserToken,
              bk_account_id: latest.current.bk_account_id,
              vtex_user_id: latest.current.vtex_user_id,
              emailHash: latest.current.emailHash,
            }),
          })
        }
      })

      let confirmed: typeof toAdd = []

      if (toAdd.length > 0) {
        try {
          await addCartItems(
            toAdd.map(({ item }) => item),
            latest.current.cartId
          )
          confirmed = toAdd
        } catch (batchError) {
          // ...con fallback item per item, per sapere quali hanno fallito
          console.error(
            'cart.addItems batch failed, retrying one by one',
            batchError
          )

          for (const entry of toAdd) {
            if (signal.aborted) {
              break
            }

            try {
              // eslint-disable-next-line no-await-in-loop
              await addCartItem(entry.item, latest.current.cartId)
              confirmed.push(entry)
            } catch (error) {
              console.error('cart.addItems failed for sku', entry.sku, error)
              result.failed.push({ sku: entry.sku, reason: 'error' })
            }
          }
        }
      }

      for (const { sku, qty, item } of confirmed) {
        // eslint-disable-next-line no-await-in-loop
        await latest.current.addItem(item)
        result.added.push({ sku, qty })
        addedItems.push(item)
      }

      try {
        trackAddedItems(
          addedItems,
          latest.current.cartId,
          startItems,
          latest.current.currencyCode as CurrencyCode,
          listName
        )
      } catch (error) {
        console.error('cart.addItems analytics failed', error)
      }

      return result
    }

    const addItems = (
      items: AddItemsInput[],
      options: AddItemsOptions = {}
    ): Promise<AddItemsResult> => {
      const listName = options.listName ?? null
      const inputs = sanitizeAddItemsInput(items)
      const signal = { aborted: false }

      const job = queue.then(() => run(inputs, listName, signal))

      queue = job.catch(() => undefined)

      let timer: ReturnType<typeof setTimeout>
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          signal.aborted = true
          reject(new Error('cart.addItems timeout'))
        }, ADD_ITEMS_TIMEOUT_MS)
      })

      return Promise.race([job, timeout])
        .then((result) => {
          window.dispatchEvent(
            new CustomEvent(CART_UPDATED_EVENT, { detail: result })
          )

          return result
        })
        .finally(() => clearTimeout(timer))
    }

    window.arcaplanet = {
      ...window.arcaplanet,
      cart: { ...window.arcaplanet?.cart, addItems },
    }

    return () => {
      if (window.arcaplanet?.cart?.addItems === addItems) {
        window.arcaplanet.cart = undefined
      }
    }
  }, [])

  return null
}

export default CartApiHandler
